import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  DocumentIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  XMarkIcon,
  CloudArrowUpIcon,
  FolderIcon,
  Bars3Icon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { useFamilyStore } from '../../store/familyStore';
import api from '../../api/client';
import { useMarkCategoryRead } from '../../hooks/useMarkCategoryRead';

// Component to fetch and display authenticated image thumbnails
function AuthenticatedThumbnail({ docId, alt }: { docId: string; alt: string }) {
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    
    const fetchThumbnail = async () => {
      try {
        const response = await api.get(`/documents/${docId}/download`, {
          responseType: 'blob',
        });
        objectUrl = URL.createObjectURL(response.data);
        setThumbUrl(objectUrl);
      } catch (err) {
        console.error('Failed to load thumbnail:', err);
        setError(true);
      }
    };

    fetchThumbnail();

    // Cleanup object URL on unmount
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [docId]);

  if (error || !thumbUrl) {
    return <span>🖼️</span>;
  }

  return (
    <img
      src={thumbUrl}
      alt={alt}
      className="w-full h-full object-cover"
    />
  );
}

interface Document {
  id: string;
  filename: string;
  original_filename: string;
  mime_type: string;
  file_size: number;
  title: string | null;
  description: string | null;
  category: string | null;
  tags: string[];
  uploaded_by_name: string | null;
  folder_id?: string | null;
  created_at: string;
}

interface DocumentFolder {
  id: string;
  name: string;
  parent_id: string | null;
  created_at: string;
  updated_at: string;
}

const apiBase = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

const formatName = (name: string) => {
  if (name.length <= 20) return name;
  return `${name.slice(0, 17)}...`;
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(mimeType: string): string {
  if (mimeType.startsWith('image/')) return '🖼️';
  if (mimeType.includes('pdf')) return '📄';
  if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return '📊';
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return '📽️';
  if (mimeType.startsWith('video/')) return '🎬';
  if (mimeType.startsWith('audio/')) return '🎵';
  return '📁';
}

export default function Documents() {
  const queryClient = useQueryClient();
  const { currentFamily } = useFamilyStore();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
   const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const viewMode: 'grid' = 'grid';
  const [currentFolder, setCurrentFolder] = useState<DocumentFolder | null>(null);
  
  // Mark document notifications as read when visiting this page
  useMarkCategoryRead('documents');
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [folderAction, setFolderAction] = useState<'create' | 'rename'>('create');
  const [folderTarget, setFolderTarget] = useState<DocumentFolder | null>(null);
  const [folderMenuFor, setFolderMenuFor] = useState<string | null>(null);
  const [renamingDoc, setRenamingDoc] = useState<Document | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [fileMenuFor, setFileMenuFor] = useState<string | null>(null);

  const { data: documents, isLoading } = useQuery({
    queryKey: ['documents', currentFamily?.id, currentFolder?.id],
    queryFn: async () => {
      if (!currentFamily?.id) return [];
      const response = await api.get(`/documents/family/${currentFamily.id}`, {
        params: { folder_id: currentFolder?.id || undefined },
      });
      return response.data as Document[];
    },
    enabled: !!currentFamily?.id,
  });

  const { data: folders } = useQuery({
    queryKey: ['folders', currentFamily?.id, currentFolder?.id],
    queryFn: async () => {
      if (!currentFamily?.id) return [];
      const response = await api.get(`/documents/family/${currentFamily.id}/folders`, {
        params: { parent_id: currentFolder?.id || undefined },
      });
      return response.data as DocumentFolder[];
    },
    enabled: !!currentFamily?.id,
  });

  // Fetch all folders to build breadcrumb path (fetch without parent_id filter to get all)
  const { data: allFolders } = useQuery({
    queryKey: ['allFolders', currentFamily?.id],
    queryFn: async () => {
      if (!currentFamily?.id) return [];
      // Fetch all folders recursively
      const allFoldersList: DocumentFolder[] = [];
      const fetchFolders = async (parentId: string | null = null) => {
        const response = await api.get(`/documents/family/${currentFamily.id}/folders`, {
          params: parentId ? { parent_id: parentId } : {},
        });
        const folders = response.data as DocumentFolder[];
        allFoldersList.push(...folders);
        // Recursively fetch subfolders
        for (const folder of folders) {
          await fetchFolders(folder.id);
        }
      };
      await fetchFolders();
      return allFoldersList;
    },
    enabled: !!currentFamily?.id,
  });

  // Build breadcrumb path
  const breadcrumbPath = React.useMemo(() => {
    if (!currentFolder || !allFolders) return [];
    
    const folderMap = new Map<string, DocumentFolder>();
    allFolders.forEach(folder => folderMap.set(folder.id, folder));
    
    const path: DocumentFolder[] = [];
    let current: DocumentFolder | undefined = currentFolder;
    
    while (current) {
      path.unshift(current);
      if (current.parent_id) {
        current = folderMap.get(current.parent_id);
      } else {
        current = undefined;
      }
    }
    
    return path;
  }, [currentFolder, allFolders]);

  const handleUpload = async () => {
    if (!selectedFile || !currentFamily?.id) return;
    setUploading(true);

    const formData = new FormData();
    formData.append('file', selectedFile);
    if (description) formData.append('description', description);
    if (currentFolder?.id) formData.append('folder_id', currentFolder.id);

    try {
      await api.post(`/documents/family/${currentFamily.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success('Document uploaded!');
      setShowUploadModal(false);
      setSelectedFile(null);
      setDescription('');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const createFolderMutation = useMutation({
    mutationFn: async (name: string) => {
      if (!currentFamily?.id) return;
      await api.post(`/documents/family/${currentFamily.id}/folders`, {
        name,
        parent_id: currentFolder?.id || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folders'] });
      toast.success('Folder created');
      setShowFolderModal(false);
      setFolderName('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create folder');
    },
  });

  const renameFolderMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      await api.patch(`/documents/folders/${id}`, { name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folders'] });
      toast.success('Folder renamed');
      setShowFolderModal(false);
      setFolderName('');
      setFolderTarget(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to rename folder');
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/documents/folders/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folders'] });
      toast.success('Folder deleted');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete folder');
    },
  });

  const handleDownload = async (doc: Document) => {
    try {
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/documents/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success('Document deleted');
    },
  });

  const renameDocMutation = useMutation({
    mutationFn: async ({ id, title }: { id: string; title: string }) => {
      await api.patch(`/documents/${id}`, { title });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success('Renamed');
      setRenamingDoc(null);
      setRenameValue('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Rename failed');
    },
  });

  const moveDocMutation = useMutation({
    mutationFn: async ({ id, folder_id }: { id: string; folder_id: string | null }) => {
      await api.patch(`/documents/${id}`, { folder_id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success('Moved');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Move failed');
    },
  });

  const filteredDocs = documents?.filter((doc) =>
    (doc.title || doc.original_filename).toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!currentFamily) {
    return (
      <div className="page-content">
        <div className="card text-center py-16">
          <DocumentIcon className="w-16 h-16 mx-auto mb-4 text-surface-500" />
          <h2 className="text-xl font-bold text-white mb-2">No Family Selected</h2>
          <p className="text-surface-400 mb-6">Select or create a family to manage documents</p>
          <Link to="/families" className="btn-primary inline-flex">Go to Families</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content overflow-x-hidden overflow-visible relative min-h-[70vh]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-white truncate">Documents</h1>
          <p className="text-surface-400 truncate">{currentFamily.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setFolderAction('create');
              setFolderTarget(null);
              setFolderName('');
              setShowFolderModal(true);
            }}
            className="btn-secondary"
          >
            <FolderIcon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">New folder</span>
          </button>
          <button onClick={() => setShowUploadModal(true)} className="btn-primary">
            <PlusIcon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Upload</span>
          </button>
        </div>
      </div>

      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-sm text-surface-400 mb-4 flex-wrap overflow-x-hidden">
        <button
          className={`hover:text-white shrink-0 ${!currentFolder ? 'text-white font-semibold' : ''}`}
          onClick={() => setCurrentFolder(null)}
        >
          /
        </button>
        {breadcrumbPath.map((folder, index) => (
          <React.Fragment key={folder.id}>
            <span className="text-surface-600 shrink-0">/</span>
            <button
              className={`hover:text-white truncate min-w-0 ${
                index === breadcrumbPath.length - 1 ? 'text-white font-semibold' : ''
              }`}
              onClick={() => setCurrentFolder(folder)}
              title={folder.name}
            >
              {folder.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
        <input
          type="text"
          placeholder="Search documents..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input pl-12"
        />
      </div>

      {/* Documents */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
        </div>
      ) : (folders?.length || 0) + (filteredDocs?.length || 0) === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card text-center py-16"
        >
          <DocumentIcon className="w-16 h-16 mx-auto mb-4 text-surface-500" />
          <h2 className="text-xl font-bold text-white mb-2">No Documents</h2>
          <p className="text-surface-400 mb-6">Upload your first document or create a folder</p>
          <div className="flex items-center justify-center gap-3">
            <button onClick={() => setShowUploadModal(true)} className="btn-primary inline-flex">
              <PlusIcon className="w-3.5 h-3.5" />
              <span>Upload Document</span>
            </button>
            <button onClick={() => setShowFolderModal(true)} className="btn-secondary inline-flex">
              <FolderIcon className="w-3.5 h-3.5" />
              <span>Create folder</span>
            </button>
          </div>
        </motion.div>
      ) : (
        <div className="space-y-3">
          {folders?.map((folder) => (
            <motion.div
              key={folder.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`card w-full hover:border-primary-500/30 transition-colors relative overflow-visible ${folderMenuFor === folder.id ? 'z-[2147483646]' : ''}`}
            >
              <button
                onClick={() => setCurrentFolder(folder)}
                className="w-full text-left flex items-start gap-3 min-w-0"
              >
                <div className="w-12 h-12 rounded-2xl bg-surface-800 flex items-center justify-center text-2xl shrink-0">
                  <FolderIcon className="w-6 h-6 text-primary-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-white truncate hover:text-primary-300 max-w-full">
                    {folder.name}
                  </div>
                  <p className="text-xs text-surface-500 mt-1">Updated {new Date(folder.updated_at).toLocaleDateString()}</p>
                </div>
                <div className="relative z-[9999] ml-auto">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFolderMenuFor(folderMenuFor === folder.id ? null : folder.id);
                      setFileMenuFor(null);
                    }}
                    className="btn-icon text-surface-400 hover:text-white"
                    title="Folder actions"
                  >
                    <Bars3Icon className="w-5 h-5" />
                  </button>
                  {folderMenuFor === folder.id && (
                    <div className="absolute right-0 top-full mt-2 w-40 rounded-xl border border-white/10 bg-surface-900/95 p-2 shadow-2xl z-[2147483647]">
                      <button
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 text-sm text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentFolder(folder);
                          setFolderMenuFor(null);
                        }}
                      >
                        Open
                      </button>
                      <button
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 text-sm text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFolderAction('rename');
                          setFolderTarget(folder);
                          setFolderName(folder.name);
                          setShowFolderModal(true);
                          setFolderMenuFor(null);
                        }}
                      >
                        Rename
                      </button>
                      <button
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 text-sm text-red-400"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFolderMenuFor(null);
                          deleteFolderMutation.mutate(folder.id);
                        }}
                      >
                        Delete
                      </button>
                      <button
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 text-sm text-surface-400"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFolderMenuFor(null);
                        }}
                      >
                        Close
                      </button>
                    </div>
                  )}
                </div>
              </button>
            </motion.div>
          ))}

          {filteredDocs?.map((doc) => {
            const isImage = doc.mime_type.startsWith('image/');
            const shortName = formatName(doc.title || doc.original_filename);
            return (
            <motion.div
              key={doc.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`card w-full flex items-start gap-3 hover:border-primary-500/30 transition-colors relative overflow-visible ${fileMenuFor === doc.id ? 'z-[2147483646]' : ''}`}
            >
              <div className="w-12 h-12 rounded-2xl bg-surface-800 flex items-center justify-center text-2xl shrink-0 overflow-hidden">
                {isImage ? (
                  <AuthenticatedThumbnail docId={doc.id} alt={doc.original_filename} />
                ) : (
                  getFileIcon(doc.mime_type)
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-white break-words line-clamp-2" title={doc.title || doc.original_filename}>
                    {shortName}
                  </h3>
                  <span className="text-xs text-surface-500 uppercase whitespace-nowrap">
                    {doc.mime_type.split('/').pop()}
                  </span>
                </div>
                {doc.description && (
                  <p className="text-sm text-surface-400 line-clamp-1 break-words">{doc.description}</p>
                )}
                <div className="flex items-center gap-3 text-xs text-surface-500 mt-2">
                  <span>{formatFileSize(doc.file_size)}</span>
                  {doc.uploaded_by_name && <span>by {doc.uploaded_by_name}</span>}
                  <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="ml-auto relative z-[9999]">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFileMenuFor(fileMenuFor === doc.id ? null : doc.id);
                    setFolderMenuFor(null);
                  }}
                  className="btn-icon text-surface-400 hover:text-white"
                  title="File actions"
                >
                  <Bars3Icon className="w-5 h-5" />
                </button>
                {fileMenuFor === doc.id && (
                  <div className="absolute right-0 top-full mt-2 w-44 rounded-xl border border-white/10 bg-surface-900/95 p-2 shadow-2xl z-[2147483647]">
                    <button
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 text-sm text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRenamingDoc(doc);
                        setRenameValue(doc.title || doc.original_filename);
                        setFileMenuFor(null);
                      }}
                    >
                      Rename
                    </button>
                    {currentFolder && (
                      <button
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 text-sm text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveDocMutation.mutate({ id: doc.id, folder_id: null });
                          setFileMenuFor(null);
                        }}
                      >
                        Move to root
                      </button>
                    )}
                    <button
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 text-sm text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(doc);
                        setFileMenuFor(null);
                      }}
                    >
                      Download
                    </button>
                    <button
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 text-sm text-red-400"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteMutation.mutate(doc.id);
                        setFileMenuFor(null);
                      }}
                    >
                      Delete
                    </button>
                    <button
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 text-sm text-surface-400"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFileMenuFor(null);
                      }}
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )})}
        </div>
      )}

      {/* Upload Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setShowUploadModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Upload Document</h2>
                <button onClick={() => setShowUploadModal(false)} className="btn-icon">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <input
                type="file"
                ref={fileInputRef}
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="hidden"
              />

              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-surface-600 rounded-2xl p-8 text-center cursor-pointer hover:border-primary-500 transition-colors"
              >
                <CloudArrowUpIcon className="w-12 h-12 mx-auto mb-4 text-surface-400" />
                {selectedFile ? (
                  <div>
                    <p className="text-white font-medium">{selectedFile.name}</p>
                    <p className="text-sm text-surface-400">{formatFileSize(selectedFile.size)}</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-white font-medium">Click to select file</p>
                    <p className="text-sm text-surface-400">or drag and drop</p>
                  </div>
                )}
              </div>

              <div className="space-y-2 mt-4">
                <label className="input-label">Description (optional)</label>
                <textarea
                  className="input resize-none"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add a short note about this file"
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFile(null);
                    setDescription('');
                  }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={!selectedFile || uploading}
                  className="btn-primary flex-1"
                >
                  {uploading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    'Upload'
                  )}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Folder modal */}
      <AnimatePresence>
        {showFolderModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => {
                setShowFolderModal(false);
                setFolderName('');
                setFolderTarget(null);
                setFolderAction('create');
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">
                  {folderAction === 'create' ? 'New folder' : 'Rename folder'}
                </h3>
                <button
                  onClick={() => {
                    setShowFolderModal(false);
                    setFolderName('');
                    setFolderTarget(null);
                    setFolderAction('create');
                  }}
                  className="btn-icon"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-2">
                <label className="input-label">Folder name</label>
                <input
                  className="input"
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  placeholder="Family documents"
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowFolderModal(false);
                    setFolderName('');
                    setFolderTarget(null);
                    setFolderAction('create');
                  }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!folderName.trim()) {
                      toast.error('Folder name required');
                      return;
                    }
                    if (folderAction === 'create') {
                      createFolderMutation.mutate(folderName.trim());
                    } else if (folderTarget) {
                      renameFolderMutation.mutate({ id: folderTarget.id, name: folderName.trim() });
                    }
                  }}
                  className="btn-primary flex-1"
                  disabled={createFolderMutation.isPending || renameFolderMutation.isPending}
                >
                  {createFolderMutation.isPending || renameFolderMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Rename document modal */}
      <AnimatePresence>
        {renamingDoc && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setRenamingDoc(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Rename file</h3>
                <button onClick={() => setRenamingDoc(null)} className="btn-icon">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-2">
                <label className="input-label">Name</label>
                <input
                  className="input"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                />
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setRenamingDoc(null);
                    setRenameValue('');
                  }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!renameValue.trim()) {
                      toast.error('Name required');
                      return;
                    }
                    renameDocMutation.mutate({ id: renamingDoc.id, title: renameValue.trim() });
                  }}
                  className="btn-primary flex-1"
                  disabled={renameDocMutation.isPending}
                >
                  {renameDocMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

